import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { registerRequest } from "../../../services/auth";
import { useState, useMemo } from "react";
import { FiEye, FiEyeOff, FiArrowRight, FiGift } from "react-icons/fi";
import { useTranslation } from "react-i18next";

import PhoneInput from "../../../components/common/phoneInput/PhoneInput";
import TermsModal from "./components/TermsModal";
import { getCanonicalRootHost } from "../../../utils/host";
import styles from "./RegisterPage.module.css";

const SLUG_REGEX = /^[a-z](?:[a-z0-9]|-(?=[a-z0-9])){1,28}[a-z0-9]$/;
const PHONE_LOCAL_REGEX = /^[1-9]\d{8}$/;

const trialBannerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 18px",
  marginBottom: 20,
  background: "linear-gradient(90deg, #ecfdf5 0%, #d1fae5 100%)",
  border: "1px solid #6ee7b7",
  borderRadius: 12,
  color: "#065f46",
  fontSize: 14,
  lineHeight: 1.45,
};
const trialIconStyle = {
  width: 36,
  height: 36,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 10,
  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  color: "#fff",
  fontSize: 18,
};

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Terms & Conditions gate: the user must open the modal and read to the bottom
  // (or use "scroll to bottom") before the checkbox can be ticked, and registration
  // stays disabled until it is.
  const [termsOpen, setTermsOpen] = useState(false);
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t("validation.nameRequired")),
        email: yup
          .string()
          .email(t("validation.emailFormat"))
          .required(t("validation.emailRequired")),
        password: yup
          .string()
          .required(t("validation.passwordRequired"))
          .min(8, t("validation.passwordMinLength"))
          .max(20, t("validation.passwordMaxLength"))
          .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/, t("validation.passwordComplexity")),
        confirmPassword: yup
          .string()
          .oneOf([yup.ref("password"), null], t("validation.passwordsMustMatch"))
          .required(t("validation.confirmPasswordRequired")),
        tenantName: yup.string().required(t("validation.tenantNameRequired")),
        tenantSlug: yup
          .string()
          .required(t("validation.tenantSlugRequired"))
          .min(3, t("validation.tenantSlugSize"))
          .max(25, t("validation.tenantSlugSize"))
          .matches(SLUG_REGEX, t("validation.tenantSlugFormat")),
        phone: yup
          .string()
          .required(t("validation.phoneRequired"))
          .matches(PHONE_LOCAL_REGEX, t("validation.phoneFormat")),
        address: yup.string().required(t("validation.addressRequired")),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
    defaultValues: { phone: "", tenantSlug: "" },
  });

  const rootHost = useMemo(() => getCanonicalRootHost(), []);

  const onSubmit = async (values) => {
    setError("");
    if (!acceptedTerms) {
      setError(t("auth.terms.required"));
      return;
    }
    try {
      setSubmitting(true);
      // No plan selection at signup — every new tenant starts on the free trial automatically.
      const payload = {
        name: values.name,
        email: values.email,
        password: values.password,
        tenantName: values.tenantName,
        tenantSlug: values.tenantSlug,
        phone: "+994" + values.phone,
        address: values.address,
        acceptedTerms,
      };

      const response = await registerRequest(payload);

      if (response.success && response.data?.verificationToken) {
        // Persist so a page reload during OTP entry / resend doesn't lose the
        // token (location.state is in-memory only).
        sessionStorage.setItem("gredus.registerToken", response.data.verificationToken);
        toast.info(t("toasts.registerOtpSent"));
        navigate("/register/confirm", {
          state: { verificationToken: response.data.verificationToken },
        });
      } else {
        // No OTP path — drop any stale token so a stray /register/confirm visit
        // doesn't pick up someone else's session.
        sessionStorage.removeItem("gredus.registerToken");
        toast.success(t("toasts.registerSuccess"));
        navigate("/login");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("auth.registerFailed");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTermsCheckboxChange = (e) => {
    // Make reading mandatory: if they haven't reached the bottom of the modal yet,
    // open it instead of ticking the box.
    if (!hasReadTerms) {
      setTermsOpen(true);
      return;
    }
    setAcceptedTerms(e.target.checked);
  };

  const handleAcceptTerms = () => {
    setHasReadTerms(true);
    setAcceptedTerms(true);
    setTermsOpen(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <Link to="/">
          <img src="/gredus-logo.svg" alt="logo" className={styles.logo} />
        </Link>

        <h2 className={styles.title}>{t("auth.registerCardTitle")}</h2>

        <div className={styles.formContainer}>
          <div style={trialBannerStyle} role="note">
            <div style={trialIconStyle}>
              <FiGift />
            </div>
            <div>
              <strong>
                {t("auth.trialBannerTitle", { defaultValue: "Pulsuz sınaq paketi ilə başlayın!" })}
              </strong>
              <div>
                {t("auth.trialBannerBody", {
                  defaultValue:
                    "Qeydiyyatdan sonra hesabınıza 2 imtahan, 25 tələbə, 50 AI sualı və 10 dəqiqə video daxil olan pulsuz paket əlavə olunur. İstədiyiniz vaxt aylıq abunəlik və ya birdəfəlik paket ala bilərsiniz.",
                })}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>{t("auth.nameLabel")}</label>
              <input type="text" {...register("name")} className={styles.input} placeholder={t("auth.namePlaceholder")} />
              {errors.name && <p className={styles.error}>{errors.name.message}</p>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>{t("auth.emailLabel")}</label>
              <input type="email" {...register("email")} className={styles.input} placeholder={t("auth.emailPlaceholder")} />
              {errors.email && <p className={styles.error}>{errors.email.message}</p>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>{t("auth.passwordLabel")}</label>

              <div className={styles.passwordWrap}>
                <input
                  type={showPassword ? "text" : "password"}
                  {...register("password")}
                  className={`${styles.input} ${styles.passwordInput}`}
                  placeholder={t("auth.passwordPlaceholder")}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>

              {errors.password && <p className={styles.error}>{errors.password.message}</p>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>{t("auth.repeatPassword")}</label>

              <div className={styles.passwordWrap}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  {...register("confirmPassword")}
                  className={`${styles.input} ${styles.passwordInput}`}
                  placeholder={t("auth.repeatPasswordPlaceholder")}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                >
                  {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>

              {errors.confirmPassword && <p className={styles.error}>{errors.confirmPassword.message}</p>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>{t("auth.tenantNameLabel")}</label>
              <input type="text" {...register("tenantName")} className={styles.input} placeholder={t("auth.tenantNamePlaceholder")} />
              {errors.tenantName && <p className={styles.error}>{errors.tenantName.message}</p>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="tenantSlug">{t("auth.tenantSlugLabel")}</label>
              <Controller
                name="tenantSlug"
                control={control}
                render={({ field }) => (
                  <div className={styles.slugField}>
                    <span className={`${styles.slugAffix} ${styles.slugPrefix}`}>https://</span>
                    <input
                      id="tenantSlug"
                      type="text"
                      name={field.name}
                      ref={field.ref}
                      value={field.value || ""}
                      onChange={(e) => {
                        const sanitized = e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, "")
                          .slice(0, 25);
                        field.onChange(sanitized);
                      }}
                      onBlur={field.onBlur}
                      maxLength={25}
                      className={styles.slugInput}
                      placeholder={t("auth.tenantSlugPreviewPlaceholder")}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    <span className={`${styles.slugAffix} ${styles.slugSuffix}`}>.{rootHost}</span>
                  </div>
                )}
              />
              <p className={styles.slugHint}>{t("auth.tenantSlugHint")}</p>
              {errors.tenantSlug && <p className={styles.error}>{errors.tenantSlug.message}</p>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>{t("auth.phoneLabel")}</label>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    size="lg"
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.phone?.message}
                  />
                )}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>{t("auth.addressLabel")}</label>
              <input type="text" {...register("address")} className={styles.input} placeholder={t("auth.addressPlaceholder")} />
              {errors.address && <p className={styles.error}>{errors.address.message}</p>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.termsRow}>
                <input
                  type="checkbox"
                  className={styles.termsCheckbox}
                  checked={acceptedTerms}
                  onChange={handleTermsCheckboxChange}
                />
                <span className={styles.termsText}>
                  {t("auth.terms.checkboxLabel")}{" "}
                  <button
                    type="button"
                    className={styles.termsLink}
                    onClick={() => setTermsOpen(true)}
                  >
                    {t("auth.terms.linkText")}
                  </button>
                </span>
              </label>
            </div>

            <div className={styles.actions}>
              <button type="submit" disabled={isSubmitting || submitting || !acceptedTerms} className={styles.submitBtn}>
                {isSubmitting || submitting ? t("auth.registering") : (
                  <>
                    {t("auth.submitRegister")} <FiArrowRight size={20} />
                  </>
                )}
              </button>
            </div>
            {error && <p className={styles.error}>{error}</p>}
          </form>
        </div>

        <p className={styles.loginText}>
          {t("auth.alreadyHaveAccount")}{" "}
          <Link to="/login" className={styles.loginLink}>
            {t("auth.loginPrompt")}
          </Link>
        </p>
      </div>

      <TermsModal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
        onAccept={handleAcceptTerms}
      />
    </div>
  );
};

export default RegisterPage;
